"use client";

import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";

import useFilterStore from "~/state/globalFilterStore";
import { api, RouterOutputs } from "~/utils/api";

import { Calendar } from "~/components/shadcn/Calendar";
import CommunityPagePeopleTab from "~/components/v3/community-components/CommunityPagePeopleTab";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { If } from "~/components/v3/Conditional";
import { daysWithEventsMap } from "~/utils/time";
import { getCommunity } from "../actions";
import { CalendarViewEventsList } from "./CalendarViewEventsList";
import { Button } from "~/components/v3/themed/Button";
import { cn } from "~/utils/fontsConfig";
import { TabSwitcherIcons } from "./TabSwitcher";

type Props = {
    community: NonNullable<Awaited<ReturnType<typeof getCommunity>>>;
    permissions: UserPermissions;
};

type QueriedEvents = RouterOutputs["event"]["getAll"];

export const CalendarView = ({ community, permissions }: Props) => {
    const { eventFilters } = useFilterStore();
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const selectedTab = (searchParams?.get("tab") ?? "events-view") as
        | "events-view"
        | "calendar-view";

    const {
        onlyShowCurrentUserAttendingEvents,
        onlyShowCurrentUserHostingEvents,
        geoLocation,
        useDateSortOverRide,
    } = eventFilters;
    const [pastEvents, setPastEvents] = useState(false);

    const isAdminOrManager =
        (permissions.isAdmin || permissions.isManager) ?? false;

    const startOfCurrentDay = DateTime.now().startOf("day").toJSDate();
    const currentTime = DateTime.now()
        .set({
            minute: Math.floor(DateTime.now().minute / 15) * 15,
            second: 0,
            millisecond: 0,
        })
        .toJSDate();

    const { data: queriedEvents, isLoading } = api.event.getAll.useQuery(
        {
            pastEvents,
            geoLocation,
            // we have to decide how to activate this filter
            sadgeFilterOn: true,
            communityFilter: {
                id: community?.id,
                slug: community?.slug,
            },
            onlyShowCurrentUserAttendingEvents,
            onlyShowCurrentUserHostingEvents,
            startOfCurrentDay,
            currentTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            displayPinnedEvents: true,
            useDateSortOverRide,
            showAdditionalHostUserInfo: true,
            take: 300,
        },
        {
            enabled: !!community,
            refetchOnWindowFocus: false,
        },
    );

    const [filteredEvents, setFilteredEvents] = useState<QueriedEvents>([]);
    const [selectedDay, setSelectedDay] = useState<Date>();
    const [daysWithEvents, setDaysWithEvents] =
        useState<Map<string, boolean>>();

    const filterEventsByDate = useMemo(() => {
        if (!queriedEvents || !searchParams?.get("date")) return queriedEvents;

        const selectedDate = new Date(searchParams.get("date")!);
        const selectedTimestamp = selectedDate.setHours(0, 0, 0, 0);

        return queriedEvents.filter((event) => {
            const eventDate = new Date(event.startTime);
            return eventDate.setHours(0, 0, 0, 0) === selectedTimestamp;
        });
    }, [queriedEvents, searchParams?.get("date")]);

    useEffect(() => {
        if (!queriedEvents) return;

        setFilteredEvents(filterEventsByDate ?? []);
        setDaysWithEvents(daysWithEventsMap(queriedEvents));
    }, [filterEventsByDate, queriedEvents]);

    const createQueryString = useCallback(
        (date?: Date) => {
            if (!searchParams) return "";
            const params = new URLSearchParams(searchParams);
            if (date) {
                params.set("date", date.toISOString());
            } else {
                params.delete("date");
            }
            return params.toString();
        },
        [searchParams],
    );

    return (
        <>
            <div className="flex h-full w-full items-center gap-2 px-2 py-2 lg:hidden">
                <div className="flex w-max justify-end gap-1 rounded bg-v3-neutral p-1 ">
                    <Button
                        variant="tabPrimary"
                        className={cn(!pastEvents && "bg-white p-2 text-black")}
                        onClick={() => setPastEvents(false)}
                    >
                        Upcoming
                    </Button>
                    <Button
                        variant="tabPrimary"
                        className={cn(pastEvents && "bg-white p-2 text-black")}
                        onClick={() => setPastEvents(true)}
                    >
                        Past
                    </Button>
                </div>
                <TabSwitcherIcons
                    communitySlug={community.slug}
                    tab={selectedTab}
                    className="ml-auto"
                />
            </div>

            <div className="p-3">
                <div className="flex w-full justify-between gap-4">
                    <div className="flex w-full flex-col-reverse justify-between gap-3 md:flex-row">
                        <div className="flex w-full grow flex-col gap-3">
                            {filteredEvents.length > 0 && (
                                <CalendarViewEventsList
                                    queriedEvents={filteredEvents}
                                    isLoadingEvents={isLoading}
                                    order={pastEvents ? "desc" : "asc"}
                                />
                            )}
                            <If condition={!filteredEvents.length}>
                                <div className="flex w-full flex-col items-center justify-center gap-2 text-v3-N-300">
                                    <p>No events this day!</p>
                                </div>
                            </If>
                        </div>

                        <div className="hidden w-full shrink justify-center md:sticky md:top-24 md:flex md:h-fit">
                            <div className="h-max rounded-[10px] bg-v3-N-500">
                                <Calendar
                                    mode="single"
                                    selected={selectedDay}
                                    onSelect={(day) => {
                                        if (!pathname) return;
                                        if (
                                            !day ||
                                            (selectedDay &&
                                                day.getTime() ===
                                                    selectedDay.getTime())
                                        ) {
                                            setSelectedDay(undefined);
                                            const queryString =
                                                createQueryString();
                                            router.push(
                                                queryString
                                                    ? `${pathname}?${queryString}`
                                                    : pathname,
                                                {
                                                    scroll: false,
                                                },
                                            );
                                            return;
                                        }

                                        setSelectedDay(day);
                                        router.push(
                                            `${pathname}?${createQueryString(day)}`,
                                            { scroll: false },
                                        );
                                    }}
                                    modifiers={{
                                        has_events: (date) => {
                                            return !!daysWithEvents?.get(
                                                DateTime.fromJSDate(date, {
                                                    zone: "local",
                                                }).toFormat("yyyy-MM-dd"),
                                            );
                                        },
                                    }}
                                    modifiersClassNames={{
                                        has_events: "before:bg-v3-primary",
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Only show in this role IF it's desktop */}
                    <span className="hidden lg:block">
                        <CommunityPagePeopleTab
                            community={community}
                            hasPermissions={isAdminOrManager}
                            refetch={() => {}}
                        />
                    </span>
                </div>
            </div>
        </>
    );
};
